// @ts-nocheck
/**
 * Appearance Tab - Configurações de Aparência
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/state/session";
import { useTheme } from "next-themes";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export function AppearanceTab() {
  const { user } = useSession();
  const { theme, setTheme } = useTheme();
  const queryClient = useQueryClient();
  const userId = user?.id;

  const { data: config, isLoading } = useQuery({
    queryKey: ["user-appearance", userId],
    queryFn: async () => {
      if (!userId) throw new Error("Usuário não autenticado");
      
      let { data, error } = await supabase
        .from("configuracoes_usuario")
        .select("*")
        .eq("user_id" as any, userId)
        .maybeSingle() as any;

      if (error) throw error;
      
      if (!data) {
        const { data: newConfig, error: insertError } = await supabase
          .from("configuracoes_usuario")
          .insert({ user_id: userId } as any)
          .select()
          .single() as any;
        
        if (insertError) throw insertError;
        data = newConfig;
      }

      return data;
    },
    enabled: !!userId,
  });

  const updateMutation = useMutation({
    mutationFn: async (values: any) => {
      const { error } = await supabase
        .from("configuracoes_usuario")
        .update({
          ...values,
          updated_at: new Date().toISOString(),
        } as any)
        .eq("user_id" as any, userId) as any;
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-appearance", userId] });
      toast.success("✅ Aparência atualizada!");
    },
    onError: () => {
      toast.error("❌ Erro ao atualizar aparência");
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const handleThemeToggle = (checked: boolean) => {
    setTheme(checked ? "dark" : "light");
  };

  const handleFontSizeChange = (value: string) => {
    updateMutation.mutate({ tamanho_fonte: value });
    document.documentElement.style.fontSize = 
      value === "pequeno" ? "14px" : 
      value === "grande" ? "18px" : "16px";
  };

  const handleLayoutChange = (value: string) => {
    updateMutation.mutate({ layout_preferido: value });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>🎨 Aparência e Tema</CardTitle>
        <CardDescription>
          Personalize a interface do sistema
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Modo escuro 🌙</Label>
            <p className="text-xs text-muted-foreground">
              Ative o tema escuro para proteger seus olhos
            </p>
          </div>
          <Switch
            checked={theme === "dark"}
            onCheckedChange={handleThemeToggle}
          />
        </div>

        <div className="space-y-2">
          <Label>Tamanho da fonte</Label>
          <Select
            value={config?.tamanho_fonte}
            onValueChange={handleFontSizeChange}
            disabled={updateMutation.isPending}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pequeno">🔤 Pequeno</SelectItem>
              <SelectItem value="medio">🔤 Médio</SelectItem>
              <SelectItem value="grande">🔤 Grande</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Ajuste o tamanho do texto para melhor legibilidade
          </p>
        </div>

        <div className="space-y-2">
          <Label>Layout padrão</Label>
          <Select
            value={config?.layout_preferido}
            onValueChange={handleLayoutChange}
            disabled={updateMutation.isPending}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="compacto">📐 Compacto</SelectItem>
              <SelectItem value="espacado">📐 Espaçado</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Escolha como os elementos são exibidos na tela
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
